import React, { useState, useContext } from "react"
import Loader from "../../ui/loader"
import moment from "moment"
import { Button } from "../../ui/button"
import { MeetingRequests } from "../../../api/app.service"
import { UserCtx } from "../../../context/userContext"
import { SvgCheckCircle } from "../../icons"
import { useToast } from "../../../hooks/useToast"

interface Props {
  service: any
  serviceUsageHistory: any[]
  refreshUsage: () => void
  refreshServices: () => void
}

const ServiceUsageHistory: React.FC<Props> = ({
  service,
  serviceUsageHistory,
  refreshUsage,
  refreshServices,
}) => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const [showConfirm, setShowConfirm] = useState(false)
  const [sessionToComplete, setSessionToComplete] = useState<any>(null)
  const [loadingConfirm, setLoadingConfirm] = useState(false)
  const timezone = user.timezone.value

  const completedCount = serviceUsageHistory.filter(
    (s) => s.meeting_status === "Completed"
  ).length

  const sessionLimit = service?.service_details?.session?.count
  const canMarkMore = completedCount < sessionLimit

  const getDisplayStatus = (session: any) => {
    const isCompleted = session.meeting_status === "Completed"
    const now = moment()
    const endTime = moment(session.meeting_end_time)

    if (isCompleted) return "Completed"
    if (endTime.isBefore(now)) return "Incomplete"
    return "Scheduled"
  }

  const getSessionDuration = (session: any) => {
    const end = moment(session.meeting_end_time)
    const start = moment(session.meeting_start_time)
    const duration = moment.duration(end.diff(start))
    const asMinutes = duration.asMinutes()
    return `${Math.ceil(asMinutes)} mins`
  }

  return (
    <>
      {/* DESKTOP TABLE — hidden on mobile */}
      <table className="w-full hidden sm:table">
        <thead>
          <tr className="text-graySlate text-base">
            <th className="text-left pb-[8px]">DATE</th>
            <th className="text-left pb-[8px]">TIME</th>
            <th className="text-left pb-[8px]">STATUS</th>
            <th className="text-left pb-[8px]">DURATION</th>
            <th className="text-left pb-[8px]">COMPLETED</th>
          </tr>
        </thead>

        <tbody>
          {serviceUsageHistory.map((session: any) => {
            const status = getDisplayStatus(session)

            return (
              <tr
                key={session.meeting_id}
                className="text-base [&:not(:last-child)]:border-b-[1px] border-grayCloud"
              >
                <td className="text-left py-[8px]">
                  {moment(session.meeting_start_time)
                    .tz(timezone)
                    .format("dddd, MMMM DD, YYYY")}
                </td>

                <td className="text-left py-[8px]">
                  {moment(session.meeting_start_time)
                    .tz(timezone)
                    .format("hh:mm a")}{" "}
                  -{" "}
                  {moment(session.meeting_end_time)
                    .tz(timezone)
                    .format("hh:mm a")}
                </td>

                <td className="py-[8px] text-center align-middle">
                  <div
                    className={`rounded-[8px] py-[4px] px-[8px] font-bold ${
                      status === "Completed"
                        ? "bg-green text-white"
                        : status === "Incomplete"
                        ? "bg-[#FF4A00] text-white"
                        : "bg-grayMist"
                    }`}
                  >
                    {status}
                  </div>
                </td>

                <td className="text-left py-[8px]">
                  {getSessionDuration(session)}
                </td>

                <td className="py-[8px] text-center align-middle">
                  {status === "Completed" ? (
                    <SvgCheckCircle className="mx-auto" />
                  ) : canMarkMore ? (
                    <Button
                      variant="link"
                      onClick={() => {
                        setSessionToComplete(session)
                        setShowConfirm(true)
                      }}
                      className="leading-tight"
                    >
                      <span className="block">
                        Mark <br /> Completed
                      </span>
                    </Button>
                  ) : (
                    <span className="text-graySlate">—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* MOBILE STACKED VIEW */}
      <div className="sm:hidden mt-2">
        {serviceUsageHistory.map((session: any) => {
          const status = getDisplayStatus(session)
          const date = moment(session.meeting_start_time)
            .tz(timezone)
            .format("dddd, MMMM DD, YYYY")

          const time = `${moment(session.meeting_start_time)
            .tz(timezone)
            .format("hh:mm a")} - ${moment(session.meeting_end_time)
            .tz(timezone)
            .format("hh:mm a")}`

          return (
            <div
              key={session.meeting_id}
              className="border-b border-grayCloud py-3 px-1"
            >
              <div className="font-semibold text-base">{date}</div>
              <div className="text-graySlate text-sm mb-2">{time}</div>

              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center justify-start gap-4 mb-2">
                  <div
                    className={`rounded-[8px] py-[4px] px-[8px] font-bold text-sm ${
                      status === "Completed"
                        ? "bg-green text-white"
                        : status === "Incomplete"
                        ? "bg-[red] text-white"
                        : "bg-grayMist"
                    }`}
                  >
                    {status}
                  </div>

                  <div className="text-sm">{getSessionDuration(session)}</div>
                </div>
              </div>

              <div className="mt-2">
                {status === "Completed" ? (
                  <SvgCheckCircle />
                ) : canMarkMore ? (
                  <Button
                    variant="link"
                    onClick={() => {
                      setSessionToComplete(session)
                      setShowConfirm(true)
                    }}
                    className="leading-tight p-0"
                  >
                    <span className="block text-left">
                      Mark <br className="hidden sm:block" /> Completed
                    </span>
                  </Button>
                ) : (
                  <span className="text-graySlate">—</span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {serviceUsageHistory?.length === 0 && (
        <div className="sm:hidden text-grayCharcoal font-bold text-base mt-[12px]">
          No sessions exist for this service.
        </div>
      )}

      {showConfirm && (
        <div className="fixed inset-0 z-[900] bg-black/30 flex items-center justify-center">
          <div className="bg-white rounded-[16px] p-[24px] w-[360px] shadow-lg">
            <h3 className="text-center font-bold text-[18px] mb-[12px]">
              Mark Session as Completed?
            </h3>

            <p className="text-center text-graySlate mb-[20px] text-base">
              This will count the session toward the client’s usage.
            </p>

            <div className="flex items-center justify-center gap-[12px]">
              <Button variant="outline" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>

              <Button
                variant="default"
                disabled={loadingConfirm}
                onClick={async () => {
                  try {
                    setLoadingConfirm(true)

                    await MeetingRequests.markSessionCompleted({
                      meeting_id: sessionToComplete?.meeting_id,
                      service_usage_id: service?.service_usage_id,
                    })

                    await refreshUsage()
                    await refreshServices()

                    setLoadingConfirm(false)
                    setShowConfirm(false)

                    toast.success("Session marked completed.")
                  } catch (err) {
                    setLoadingConfirm(false)
                    toast.error("Something went wrong. Please try again.")
                  }
                }}
              >
                {loadingConfirm ? <Loader /> : "Confirm"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ServiceUsageHistory
